import { useEffect, useRef } from "react";
import {
  Controller,
  Control,
  FieldValues,
  Path,
  useFormContext,
} from "react-hook-form";
import { FormGroup, Label } from "reactstrap";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { Typeahead, TypeaheadRef } from "react-bootstrap-typeahead";

type OptionType = { id: number | string; name: string } & Record<string, any>;

type RHFMultiSelectSearchProps<T extends FieldValues, O extends OptionType> = {
  label: string;
  name: Path<T>;
  control?: Control<T>;
  options: O[];
  placeholder: string;
  multiple?: boolean; // ✅ if true → array of options, else single option
  labelClassName?: string;
  valueKey?: keyof O; // ✅ allow valueKey
  labelKey?: string | ((option: O) => string);
  disabled?: boolean;
};

const RHFMultiSelectSearch = <
  T extends FieldValues,
  O extends OptionType = OptionType
>({
  label,
  name,
  options,
  disabled = false,
  placeholder,
  multiple = true,
  labelKey = "name",
  labelClassName = "",
}: RHFMultiSelectSearchProps<T, O>) => {
  const { control } = useFormContext();
  const typeaheadRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        typeaheadRef.current?.hideMenu(); // 👈 use
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={containerRef}>
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <FormGroup>
            <Label>{label}</Label>
            <Typeahead
              id={name}
              ref={typeaheadRef}
              labelKey={labelKey as any}
              multiple={multiple}
              options={options}
              placeholder={placeholder}
              selected={field.value || []}
              onChange={(selected) => field.onChange(selected)}
              disabled={disabled}
            />
            {fieldState.error && (
              <div className="invalid-feedback d-block">
                {fieldState.error.message}
              </div>
            )}
          </FormGroup>
        )}
      />
    </div>
  );
};

export default RHFMultiSelectSearch;
